import { useVirtualizer } from '@tanstack/react-virtual'
import { useGetShrimpHealthThreshold } from 'modules/notifications/api/queries'
import { useGetDashboardBootstrap } from 'modules/pond/api/queries'
import { GroupedRow } from 'modules/pond/api/types'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'

import { usePondDashboardTableSelector } from '../../api/selector'
import { EmptyState } from './components/EmptyState'
import { SkeletonTable } from './components/SkeletonTable'
import { TableHeader } from './components/TableHeader'
import { VirtualRow } from './components/VirtualRow'
import { LabelGroup, ROW_HEIGHT_DEFAULT, ROW_HEIGHT_TEXTTYPE } from './constants'
import { useCellEditor } from './hooks/useCellEditor'
import { useProcessedRows } from './hooks/useProcessedRows'
import { tableStyles } from './styles'
import { hasLabelTypeText } from './utils'

type DiseaseDashboardProps = {
  setLoadingMobile?: (isLoading: boolean) => void
}

export default function TableSummaryDashboard({ setLoadingMobile }: DiseaseDashboardProps) {
  const { t } = useTranslation('pondOverview')

  const { data, isLoading, hasNextPage, fetchNextPage } =
    usePondDashboardTableSelector()
  const { data: bootstrap } = useGetDashboardBootstrap()
  // NK-273: vibrio cells are coloured by the shrimp-health alert settings.
  const { data: shrimpHealthThresholds } = useGetShrimpHealthThreshold()
  const scrollRef = useRef<HTMLDivElement>(null)
  const headerBlockRef = useRef<HTMLDivElement>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [startX, setStartX] = useState<number>(0)
  const [isPulling, setIsPulling] = useState(false)

  const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true'
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor
    const ios =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    setIsIOS(ios)
  }, [])

  const { flatRows } = useProcessedRows(data?.rows as GroupedRow[], new Map())
  const { editedCells, handleCellChange } = useCellEditor(flatRows)

  const { flatRows: processedRows } = useProcessedRows(data?.rows as GroupedRow[], editedCells)

  // Pull the อายุกุ้ง (shrimp-age) row out of the virtualized list so it can be
  // pinned in the frozen header block alongside the date row (NK-206). It stays
  // frozen when scrolling down; everything else keeps virtualizing below it.
  const ageRowIndex = useMemo(
    () => processedRows.findIndex((r) => r.group === LabelGroup.ShrimpAge),
    [processedRows]
  )
  const ageRow = ageRowIndex >= 0 ? processedRows[ageRowIndex] : undefined
  // Shrimp age per column, so water cells are judged against the threshold band
  // for that day's age (NK-272).
  const shrimpAges = useMemo(
    () =>
      ageRow?.values.map((v) => {
        const age = Number(v)
        return v === '' || v === null || v === undefined || Number.isNaN(age) ? undefined : age
      }),
    [ageRow]
  )
  const bodyRows = useMemo(
    () => (ageRowIndex >= 0 ? processedRows.filter((_, i) => i !== ageRowIndex) : processedRows),
    [processedRows, ageRowIndex]
  )

  const rowVirtualizer = useVirtualizer({
    count: bodyRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (idx) =>
      hasLabelTypeText(bodyRows[idx]?.label) ? ROW_HEIGHT_TEXTTYPE : ROW_HEIGHT_DEFAULT,
    overscan: isMobile ? 4 : 10,
  })

  // NK-305: the spinner and the double-click guard use only this component's own
  // isLoadingMore. React Query's isFetchingNextPage is computed optimistically on
  // the first render after a remount, from the fetchMeta the previous
  // fetchNextPage left behind, so after "load more" → another tab → back it read
  // true; when the refetch then came back unchanged nothing re-rendered, and the
  // button kept spinning (and stayed disabled).
  const handleLoadMore = useCallback(async () => {
    if (!hasNextPage || isLoadingMore) return

    setIsLoadingMore(true)
    try {
      await fetchNextPage()
    } finally {
      setIsLoadingMore(false)
    }
  }, [hasNextPage, isLoadingMore, fetchNextPage])

  useEffect(() => {
    if (!isMobile || !scrollRef.current) return

    const scrollElement = scrollRef.current
    let startX = 0
    let startY = 0
    let isHorizontalMove = false

    const handleTouchStart = (e: TouchEvent) => {
      if (scrollElement.scrollLeft === 0) {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        isHorizontalMove = false
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!startX || !startY) return

      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const diffX = currentX - startX
      const diffY = currentY - startY

      if (Math.abs(diffX) > Math.abs(diffY)) {
        isHorizontalMove = true
      }

      if (isHorizontalMove && diffX > 50 && scrollElement.scrollLeft === 0) {
        setIsPulling(true)
      }
    }

    const handleTouchEnd = async () => {
      if (isPulling && hasNextPage) {
        await handleLoadMore()
      }
      startX = 0
      startY = 0
      isHorizontalMove = false
      setIsPulling(false)
    }

    scrollElement.addEventListener('touchstart', handleTouchStart)
    scrollElement.addEventListener('touchmove', handleTouchMove)
    scrollElement.addEventListener('touchend', handleTouchEnd)

    return () => {
      scrollElement.removeEventListener('touchstart', handleTouchStart)
      scrollElement.removeEventListener('touchmove', handleTouchMove)
      scrollElement.removeEventListener('touchend', handleTouchEnd)
    }
  }, [hasNextPage, handleLoadMore, isPulling, isMobile])

  // NK-305: cells save on blur, and leaving the page does not always blur them —
  // on Android, switching to the photo picker or another app left the typed
  // value unsent, and it was gone if the WebView was reloaded meanwhile. Blur
  // the cell being edited when the page is hidden or closed so it saves.
  useEffect(() => {
    const flushFocusedCell = () => {
      const active = document.activeElement
      if (active instanceof HTMLElement && scrollRef.current?.contains(active)) {
        active.blur()
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushFocusedCell()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', flushFocusedCell)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', flushFocusedCell)
    }
  }, [])

  useEffect(() => {
    if (isMobile) {
      if (setLoadingMobile) {
        setLoadingMobile(isLoading || isLoadingMore)
      }
    }
  }, [isMobile, isIOS, isLoading, isLoadingMore, setLoadingMobile])

  useEffect(() => {
    if (!isMobile || !isIOS || !scrollRef.current) return

    const scrollElement = scrollRef.current

    const handleScroll = () => {
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth
      const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight

      if (scrollElement.scrollLeft < 0) {
        scrollElement.scrollLeft = 0
      } else if (scrollElement.scrollLeft > maxScrollLeft) {
        scrollElement.scrollLeft = maxScrollLeft
      }

      if (scrollElement.scrollTop < 0) {
        scrollElement.scrollTop = 0
      } else if (scrollElement.scrollTop > maxScrollTop) {
        scrollElement.scrollTop = maxScrollTop
      }
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [isMobile, isIOS])

  // NK-206: On Android WebView the frozen header/อายุกุ้ง block is rendered with
  // CSS `zoom`, and Blink there miscomputes `position: sticky` under zoom (no
  // freeze when zoomed out, freeze released mid-scroll when zoomed in). iOS uses
  // `transform` scaling where sticky works, so it keeps the CSS sticky path.
  // For the non-iOS (zoom) path we drive the freeze in JS instead: translate the
  // block by the scroller's scrollTop so it stays pinned. scrollTop and the
  // transform share the same pre-zoom coordinate space, so a plain
  // translateY(scrollTop) is correct at every zoom level (verified in Blink).
  useEffect(() => {
    if (!isMobile || isIOS) return
    const scrollElement = scrollRef.current
    const block = headerBlockRef.current
    if (!scrollElement || !block) return

    let rafId = 0
    const apply = () => {
      rafId = 0
      block.style.transform = `translateY(${scrollElement.scrollTop}px)`
    }
    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(apply)
    }

    apply() // pin correctly on first paint / re-mount
    scrollElement.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener('scroll', onScroll)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isMobile, isIOS, isLoading, data?.rows])

  if (isLoading) return <SkeletonTable />

  if (!data?.rows || data.rows.length === 0) return <EmptyState />

  return (
    <div style={{ position: 'relative', height: isMobile ? '100%' : undefined }}>
      {/* On the mobile webview the table is scaled by a transform wrapper, which
          does not affect layout. The bounded height above makes the inner
          scroller (scrollRef) a real viewport inside the scaled subtree —
          otherwise scrolling collapses and bottom rows get clipped. */}
      <div ref={scrollRef} style={tableStyles.container(isMobile, isIOS)}>
        <div style={tableStyles.tableWrapper}>
          <div
            ref={headerBlockRef}
            style={
              isIOS
                ? tableStyles.stickyHeaderBlock
                : { ...tableStyles.stickyHeaderBlock, position: 'relative' as const, top: 'auto' }
            }
          >
            <TableHeader
              columns={data.columns}
              hasNextPage={!isMobile && hasNextPage}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
            />

            {ageRow && (
              <VirtualRow
                pinned
                row={ageRow}
                rowHeight={
                  hasLabelTypeText(ageRow.label) ? ROW_HEIGHT_TEXTTYPE : ROW_HEIGHT_DEFAULT
                }
                translateY={0}
                onCellChange={handleCellChange}
                editedCells={editedCells}
                bootstrap={bootstrap}
              />
            )}
          </div>

          {isMobile && isPulling && (
            <div
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1000,
                color: palette.light.primary.main,
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {t('lab.table.releaseToLoad')}
            </div>
          )}

          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = bodyRows[virtualRow.index]

              return (
                <VirtualRow
                  key={virtualRow.index}
                  row={row}
                  rowHeight={virtualRow.size}
                  translateY={virtualRow.start}
                  onCellChange={handleCellChange}
                  editedCells={editedCells}
                  bootstrap={bootstrap}
                  shrimpAges={shrimpAges}
                  shrimpHealthThresholds={shrimpHealthThresholds}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
